import * as React from 'react';

import { enumPurchaseOrderStatus } from '@rematter/frontend-lib-api-client';
import { Button, cn, ContentCard } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { PurchaseOrderWizardStepContractInfo } from './PurchaseOrderWizardStepContractInfo';

import {
  $currentDraftId,
  $purchaseOrderForm,
  $purchaseOrderIsMissingRequired,
  $resetPurchaseOrderForm,
  $setPurchaseOrderForm,
  $updatePurchaseOrder,
  $validatePurchaseOrderForm,
  $validatePurchaseOrderFormDraft,
  PurchaseOrderFormState,
} from '~/modules/trade/_state/purchaseOrderForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { PurchaseOrderRecord } from '~/modules/trade/types/purchaseOrder';
import { maybeParseCalendarDate } from '~/shared/utils/dateTime';

type Props = {
  purchaseOrder?: PurchaseOrderRecord | null;
  onDiscard?: () => void;
  onSaveUpdates?: () => void;
  isSaving?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'embedded';
};

const purchaseOrderToFormState = (
  purchaseOrder: PurchaseOrderRecord,
): PurchaseOrderFormState => ({
  id: purchaseOrder.id,
  facilityId: purchaseOrder.facility?.id ?? null,
  purchaseOrderStartDate:
    maybeParseCalendarDate(purchaseOrder.startDate?.toString()) ?? null,
  purchaseOrderEndDate:
    maybeParseCalendarDate(purchaseOrder.endDate?.toString()) ?? null,
  purchaseOrderNumber: purchaseOrder.documentNumber,
  accountRepresentativeId: purchaseOrder.accountRepresentative?.id || null,
  counterpartyReferenceNumber: purchaseOrder.counterpartyReferenceNumber || '',

  supplierId: purchaseOrder.account?.id ?? null,
  contactId: purchaseOrder.contact?.id || null,
  supplierAddressId: purchaseOrder.accountAddress?.id || null,

  paymentCurrency: purchaseOrder.paymentCurrency,
  paymentTerm: purchaseOrder.paymentTerm,
  freightTerm: purchaseOrder.freightTerm,

  lineItems: purchaseOrder.lineItems || [],

  notes: purchaseOrder.notes || '',
  completionNotes: purchaseOrder.completionNotes || '',

  isSubmitting: false,
  errors: {},
});

export const PurchaseOrderTabContractInfo: React.FC<Props> = ({
  purchaseOrder,
  onDiscard,
  onSaveUpdates,
  isSaving = false,
  disabled = false,
  variant = 'default',
}) => {
  const t = useTranslations();

  const [isUpdating, setIsUpdating] = React.useState(false);
  const [originalFormState, setOriginalFormState] =
    React.useState<PurchaseOrderFormState | null>(null);

  const isMissingRequired = useAtomValue($purchaseOrderIsMissingRequired);

  const [form] = useAtom($purchaseOrderForm);
  const [, setPurchaseOrderForm] = useAtom($setPurchaseOrderForm);
  const [, resetForm] = useAtom($resetPurchaseOrderForm);
  const [, validateForm] = useAtom($validatePurchaseOrderForm);
  const [, validateFormDraft] = useAtom($validatePurchaseOrderFormDraft);

  const updatePurchaseOrderMutation = useSetAtom($updatePurchaseOrder);
  const setCurrentDraftId = useSetAtom($currentDraftId);

  const toast = useToast();

  const isDraftStatus = purchaseOrder?.status === enumPurchaseOrderStatus.Draft;

  const purchaseOrderId = purchaseOrder?.id;

  React.useEffect(() => {
    if (purchaseOrder) {
      const formState = purchaseOrderToFormState(purchaseOrder);
      setCurrentDraftId(purchaseOrder.id);
      setPurchaseOrderForm(formState);
      setOriginalFormState(formState);
    } else {
      resetForm();
      setOriginalFormState(null);
    }
  }, [purchaseOrderId, setPurchaseOrderForm, setCurrentDraftId, resetForm]);

  const handleSave = async () => {
    if (!purchaseOrder) return;

    const validation =
      isMissingRequired && isDraftStatus ? validateFormDraft() : validateForm();
    if (!validation.isValid) {
      toast.error(t.shared.fixValidationErrors);
      return;
    }

    try {
      setIsUpdating(true);
      setPurchaseOrderForm({ isSubmitting: true });

      const updateData = {
        id: purchaseOrder.id,
        documentNumber: form.purchaseOrderNumber || undefined,
        startDate: form.purchaseOrderStartDate,
        endDate: form.purchaseOrderEndDate,
        accountId: form.supplierId || undefined,
        counterpartyReferenceNumber:
          form.counterpartyReferenceNumber || undefined,
        accountContactId: form.contactId || undefined,
        facilityId: form.facilityId || undefined,
        accountRepresentativeId: form.accountRepresentativeId || undefined,
        accountAddressId: form.supplierAddressId || undefined,
        paymentCurrency: form.paymentCurrency || undefined,
        freightTerm: form.freightTerm || undefined,
        paymentTerm: form.paymentTerm || undefined,
        notes: form.notes || undefined,
        completionNotes: form.completionNotes || undefined,
      };

      await updatePurchaseOrderMutation(updateData);

      toast.success(t.purchaseOrderDetail.messages.updateSuccess);

      setOriginalFormState({ ...form });

      onSaveUpdates?.();
    } catch (error) {
      toast.error(t.purchaseOrderDetail.messages.updateError);
      console.error('Failed to update purchase order:', error);
    } finally {
      setPurchaseOrderForm({ isSubmitting: false });
      setIsUpdating(false);
    }
  };

  const handleDiscard = () => {
    if (originalFormState) {
      setPurchaseOrderForm(originalFormState);
    } else {
      resetForm();
    }
    onDiscard?.();
  };

  const isFormChanged =
    originalFormState &&
    JSON.stringify(form) !== JSON.stringify(originalFormState);
  const isLoading = isUpdating || isSaving;

  if (!purchaseOrder) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-white p-6">
          <div className="py-8 text-center">
            <p className="text-neutral/50">
              {t.shared.noPurchaseOrderSelected}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        '@PurchaseOrderTabContractInfo flex flex-col gap-5',
        variant !== 'embedded' && 'p-5',
      )}
    >
      <ContentCard
        className={cn(variant === 'embedded' && 'rounded-none border-none')}
      >
        <div className="flex-1">
          <PurchaseOrderWizardStepContractInfo disabled={disabled} />
        </div>
        {!disabled && (
          <div className="sticky bottom-0 border-t border-neutral/10 bg-white py-4">
            <div className="flex items-center justify-end">
              <div className="flex gap-3">
                <Button
                  color="neutral"
                  variant="outline"
                  onPress={handleDiscard}
                  isDisabled={isLoading}
                >
                  {t.purchaseOrderDetail.actions.discard}
                </Button>
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleSave}
                  isPending={isLoading}
                  isLoading={isLoading}
                  isDisabled={!isFormChanged || !purchaseOrder}
                >
                  {isMissingRequired && isDraftStatus
                    ? t.loadDetail.footer.saveDraft
                    : t.purchaseOrderDetail.actions.saveUpdates}
                </Button>
              </div>
            </div>
          </div>
        )}
      </ContentCard>
    </div>
  );
};
